import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bcc/components/ui/alert-dialog";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { useToast } from "@bcc/hooks/use-toast";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { setMapProductsStepTestId } from "./map-products-step.utils";
import { OnboardingStepper } from "./onboarding-stepper";
import { ProductMappingRow } from "./product-mapping-row";

interface BeanzProduct {
  id: string;
  name: string;
}

interface PlatformProduct {
  id: string;
  name: string;
}

interface Mapping {
  beanzId: string;
  platformId: string | null;
}

// Mock API calls - simulates /api/onboarding/products and /api/onboarding/map-products
// TODO: Replace with real API calls when backend is ready

const mockBeanzProducts: BeanzProduct[] = [
  { id: "bp-1", name: "Ethiopian Yirgacheffe 12oz" },
  { id: "bp-2", name: "Colombian Supremo 1lb" },
  { id: "bp-3", name: "House Blend 5lb" },
  { id: "bp-4", name: "Sumatra Mandheling 12oz" },
  { id: "bp-5", name: "Guatemala Antigua 1lb" },
];

const mockPlatformProducts: PlatformProduct[] = [
  { id: "sp-1", name: "Ethiopian Yirgacheffe - 12oz Bag" },
  { id: "sp-2", name: "Colombian Supremo - 1lb Bag" },
  { id: "sp-3", name: "House Blend - 5lb Bulk" },
  { id: "sp-4", name: "Decaf Blend - 12oz" },
  { id: "sp-5", name: "Sumatra Dark Roast - 12oz" },
  { id: "sp-6", name: "Guatemala Single Origin - 1lb" },
];

async function mockFetchProducts(): Promise<{
  beanzProducts: BeanzProduct[];
  platformProducts: PlatformProduct[];
  platform: string;
}> {
  // Simulate network delay (1.5 seconds)
  await new Promise((resolve) => setTimeout(resolve, 1500));

  // Get platform from localStorage (set during connect step)
  const platform = localStorage.getItem("tempConnectedPlatform") || "shopify";

  return {
    beanzProducts: mockBeanzProducts,
    platformProducts: mockPlatformProducts,
    platform,
  };
}

async function mockSaveMappings(
  mappings: Mapping[],
): Promise<{ success: boolean; mappedCount: number }> {
  // Simulate network delay (2 seconds)
  await new Promise((resolve) => setTimeout(resolve, 2000));

  const mappedCount = mappings.filter((m) => m.platformId !== null).length;

  return {
    success: true,
    mappedCount,
  };
}

export interface MapProductsStepProps {
  testId?: string;
}

export function MapProductsStep({ testId }: MapProductsStepProps = {}) {
  const { t } = useTranslation();
  const [beanzProducts, setBeanzProducts] = useState<BeanzProduct[]>([]);
  const [platformProducts, setPlatformProducts] = useState<PlatformProduct[]>(
    [],
  );
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [platform, setPlatform] = useState<string>("");
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Use mock API call instead of real fetch
        const data = await mockFetchProducts();

        setBeanzProducts(data.beanzProducts);
        setPlatformProducts(data.platformProducts);
        setPlatform(data.platform);

        // Initialize mappings
        setMappings(
          data.beanzProducts.map((p: BeanzProduct) => ({
            beanzId: p.id,
            platformId: null,
          })),
        );
      } catch (error) {
        console.error("[v0] Failed to fetch products:", error);
        toast({
          title: t("onboarding.mapProducts.toasts.errorLoading"),
          description: t("onboarding.mapProducts.toasts.tryAgain"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [toast]);

  const handleMappingChange = (beanzId: string, platformId: string | null) => {
    setMappings((prev) =>
      prev.map((m) => (m.beanzId === beanzId ? { ...m, platformId } : m)),
    );
  };

  const mappedCount = mappings.filter((m) => m.platformId !== null).length;
  const totalCount = mappings.length;

  const handleContinue = async () => {
    setIsSaving(true);
    try {
      // Use mock API call instead of real fetch
      const data = await mockSaveMappings(mappings);

      if (!data.success) {
        throw new Error("Failed to save mappings");
      }

      toast({
        title: t("onboarding.mapProducts.toasts.mappedSuccess"),
        description: t("onboarding.mapProducts.toasts.productsMapped", {
          count: data.mappedCount,
        }),
      });

      navigate("/onboarding/success");
    } catch (error) {
      console.error("[v0] Failed to save mappings:", error);
      toast({
        title: t("onboarding.mapProducts.toasts.errorSaving"),
        description: t("onboarding.mapProducts.toasts.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = async () => {
    setShowSkipDialog(false);
    setIsSaving(true);
    try {
      // Use mock API call instead of real fetch
      const data = await mockSaveMappings([]);

      if (!data.success) {
        throw new Error("Failed to skip");
      }

      navigate("/onboarding/success");
    } catch (error) {
      console.error("[v0] Failed to skip:", error);
      toast({
        title: t("onboarding.mapProducts.toasts.error"),
        description: t("onboarding.mapProducts.toasts.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p
          data-testid={setMapProductsStepTestId("loading-text")}
          className="text-muted-foreground"
        >
          {t("onboarding.mapProducts.loadingProducts")}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid={testId ?? setMapProductsStepTestId("container")}
      className="flex flex-col gap-8"
    >
      <OnboardingStepper currentStep={2} />

      <div className="grid gap-8 lg:grid-cols-[300px_1fr]">
        <div className="flex flex-col gap-4">
          <div>
            <h2
              data-testid={setMapProductsStepTestId("heading")}
              className="mb-2 text-xl font-semibold text-text-primary"
            >
              {t("onboarding.mapProducts.title")}
            </h2>
            <p className="text-sm text-text-muted">
              {t("onboarding.mapProducts.description", { platform })}
            </p>
          </div>
        </div>

        <Card className="border-border-subtle shadow-sm">
          <CardContent className="p-6">
            <div className="mb-4 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="pb-3 text-left text-sm font-medium text-text-muted">
                      {t("onboarding.mapProducts.table.beanzProducts")}
                    </th>
                    <th className="pb-3 text-left text-sm font-medium text-text-muted">
                      {platform === "shopify" ? "Shopify" : "WooCommerce"}{" "}
                      {t("onboarding.mapProducts.table.products")}
                    </th>
                    <th className="pb-3 text-left text-sm font-medium text-text-muted">
                      {t("onboarding.mapProducts.table.status")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {beanzProducts.map((product) => (
                    <ProductMappingRow
                      key={product.id}
                      beanzProduct={product}
                      platformProducts={platformProducts}
                      selectedPlatformId={
                        mappings.find((m) => m.beanzId === product.id)
                          ?.platformId || null
                      }
                      onMappingChange={handleMappingChange}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-border pt-4">
              <div className="flex items-center gap-4">
                <p
                  data-testid={setMapProductsStepTestId("mapped-count")}
                  className="text-sm text-text-muted"
                >
                  {t("onboarding.mapProducts.mappedCount", {
                    mapped: mappedCount,
                    total: totalCount,
                  })}
                </p>
                <Button
                  data-testid={setMapProductsStepTestId("skip-button")}
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSkipDialog(true)}
                  disabled={isSaving}
                >
                  {t("onboarding.mapProducts.skipForNow")}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid={setMapProductsStepTestId("back-button")}
                  variant="outline"
                  onClick={() => navigate("/onboarding")}
                  disabled={isSaving}
                >
                  {t("common.back")}
                </Button>
                <Button
                  data-testid={setMapProductsStepTestId("continue-button")}
                  onClick={handleContinue}
                  disabled={mappedCount === 0 || isSaving}
                >
                  {isSaving
                    ? t("onboarding.mapProducts.saving")
                    : t("common.continue")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("onboarding.mapProducts.skipDialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("onboarding.mapProducts.skipDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSkip}>
              {t("onboarding.mapProducts.skipDialog.skipAnyway")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
